import { useSeoMeta } from '@unhead/react';
import { NostrSection } from '@/components/NostrSection';
import { LoginArea } from '@/components/auth/LoginArea';
import { Link } from 'react-router-dom';
import {
  Hand,
  Shield,
  Heart,
  Brain,
  Music,
  Users,
  Award,
  GraduationCap,
  Sword,
  CheckCircle2,
  ChevronDown,
  Sparkles,
  MessageCircle,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const HERO_IMAGE = 'https://blossom.primal.net/4bb566e7761a8f983d2ed6e203a74d5e1e9ce5b76a89bfb86aea0aad0046048f.jpg';

const benefits = [
  {
    icon: Brain,
    title: 'Improves Posture',
    description:
      'Clasping your hands behind your back naturally opens the chest, draws the shoulders back, and aligns the spine — a simple, effortless posture correction.',
  },
  {
    icon: Heart,
    title: 'Signals Openness & Confidence',
    description:
      'Body-language research consistently links the behind-the-back hand position with calm authority, openness, and approachability.',
  },
  {
    icon: Shield,
    title: 'Reduces Nervous Gestures',
    description:
      'Keeping hands occupied behind your back prevents fidgeting, touching your face, or crossing your arms — all signals of discomfort.',
  },
  {
    icon: Brain,
    title: 'Enhances Focus & Concentration',
    description:
      'Removing hands from view lets you listen and think more clearly. Many great thinkers adopted this stance while walking and reflecting.',
  },
  {
    icon: Hand,
    title: 'Stretches Shoulders & Arms',
    description:
      'Clasping the hands behind the back gently stretches the pectoral muscles, the front of the shoulders, and the biceps — a built-in micro-stretch.',
  },
  {
    icon: Award,
    title: 'Commands Respect',
    description:
      'In many cultures and professions, hands behind the back convey discipline, respect, and attentiveness — a universal mark of decorum.',
  },
];

const challenges = [
  { level: 'Beginner', title: '5-Minute Stand', desc: 'Stand with hands clasped behind your back for 5 minutes while listening to music or a podcast.' },
  { level: 'Intermediate', title: 'Hands-Behind Walk', desc: 'Take a 15-minute walk with hands clasped behind your back. Focus on an upright posture throughout.' },
  { level: 'Intermediate', title: 'Sing a Full Song', desc: 'Perform your favourite song from beginning to end with your hands behind your back — no peeking forward!' },
  { level: 'Advanced', title: 'One-Hour Discipline', desc: 'Spend one hour during your work or study day with your hands behind your back whenever you are standing.' },
  { level: 'Advanced', title: 'Choir Rehearsal', desc: 'Complete a full choir rehearsal — vocals, diction, breath — with hands clasped at the back of your waist.' },
  { level: 'Expert', title: 'Military Parade Stance', desc: 'Maintain parade rest — feet shoulder-width, hands interlocked at the small of the back — for 30 minutes.' },
];

const examples = [
  {
    icon: Music,
    category: 'Singing & Performance',
    title: 'Singing with Hands Behind Your Back',
    desc: 'Many vocal coaches encourage singers to clasp their hands behind their back during practice. It opens the chest for full diaphragmatic breathing, prevents unnecessary hand gestures that can tense the upper body, and trains the performer to express emotion through voice alone. Choristers in elite choirs routinely adopt this stance during sight-reading sessions.',
    color: 'from-purple-50 to-purple-100 dark:from-purple-950/30 dark:to-purple-900/20',
    border: 'border-purple-200 dark:border-purple-800',
  },
  {
    icon: GraduationCap,
    category: 'Schools',
    title: 'Classroom & School Tradition',
    desc: 'In many European and Asian school traditions, pupils stand with their hands behind their backs when reciting, answering questions, or greeting teachers. The posture signals readiness, respect, and attentiveness. Schools from military academies to classical music conservatories use it as a standard of discipline and deportment.',
    color: 'from-blue-50 to-blue-100 dark:from-blue-950/30 dark:to-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
  },
  {
    icon: Users,
    category: 'Choirs',
    title: 'Choral Tradition',
    desc: 'Professional and youth choirs around the world require singers to stand with hands clasped behind their backs or lightly at their sides. The hands-behind posture aligns the spine, elevates the sternum, and allows the conductor to have full eye contact with every singer — free from the distraction of wandering hands.',
    color: 'from-amber-50 to-amber-100 dark:from-amber-950/30 dark:to-amber-900/20',
    border: 'border-amber-200 dark:border-amber-800',
  },
  {
    icon: Sword,
    category: 'Military',
    title: 'Military Parade Rest',
    desc: 'Every major military force uses some version of the "parade rest" or "at ease" stance: feet shoulder-width apart, hands interlocked at the small of the back. It communicates alert readiness without aggression, keeps hands visible and controlled, and serves as a powerful symbol of discipline that has been maintained for centuries across armies worldwide.',
    color: 'from-green-50 to-green-100 dark:from-green-950/30 dark:to-green-900/20',
    border: 'border-green-200 dark:border-green-800',
  },
];

const levelColour: Record<string, string> = {
  Beginner: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  Intermediate: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  Advanced: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  Expert: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

const neurodivergent = [
  {
    heading: 'Why Some ND People Love It',
    content:
      'For autistic people and others with sensory processing differences, clasping hands behind the back provides steady proprioceptive feedback — information about where the body is in space. This input can be grounding and regulating, quieting the kind of background "noise" that an overactive sensory system can generate. Some Asperger autists and ADHDers find the position helps them focus during conversations, stand calmly in queues, or simply exist more comfortably in their own bodies.',
  },
  {
    heading: 'Stimming & Self-Regulation',
    content:
      'Many autistic people use repetitive movement — stimming — to self-regulate emotion and arousal. Keeping hands clasped behind the back is a quieter, socially inconspicuous alternative to more visible stims. It occupies the hands with steady pressure, can be maintained for long periods, and often goes entirely unnoticed by others — making it a practical tool for high-demand environments like schools, workplaces, or public transport.',
  },
  {
    heading: 'When It Feels Wrong',
    content:
      'Not every neurodivergent person experiences this the same way. For some, the position feels constraining, unnatural, or anxiety-provoking. For people with motor coordination differences, shoulder hypermobility, or specific sensory sensitivities, it may be uncomfortable or even painful. Some ND people need their hands visible and available as part of how they think and communicate. All of these responses are valid.',
  },
  {
    heading: 'Your Experience Is Your Own',
    content:
      'Neurodivergent people are experts on their own bodies and nervous systems. Whether hands behind the back is helpful, neutral, or aversive for you, that answer belongs to you alone. No preference is more autistic or more correct than another. The value of sharing these experiences is to open up space for people who might not yet have the language to describe what they already know about themselves.',
  },
];

export default function Index() {
  useSeoMeta({
    title: 'Hands Behind Your Back! — Discipline, Posture & Poise',
    description:
      'Discover the benefits of keeping your hands behind your back — from improved posture and confidence to traditions in schools, choirs, singing, and the military.',
  });

  return (
    <div className="min-h-screen bg-background text-foreground">

      {/* NAV */}
      <header className="fixed top-0 inset-x-0 z-50 bg-background/80 backdrop-blur border-b border-border/50">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <span className="font-bold text-lg tracking-tight">
            <span className="hidden sm:inline">Hands Behind Your Back!</span>
            <span className="sm:hidden">HBYB</span>
          </span>
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
            <a href="#benefits" className="text-muted-foreground hover:text-foreground transition-colors">Benefits</a>
            <a href="#examples" className="text-muted-foreground hover:text-foreground transition-colors">Examples</a>
            <a href="#challenges" className="text-muted-foreground hover:text-foreground transition-colors">Challenges</a>
            <a href="#neurodivergent" className="text-muted-foreground hover:text-foreground transition-colors">ND</a>
            <a href="#nostr" className="text-muted-foreground hover:text-foreground transition-colors">Nostr</a>
          </nav>
          <LoginArea className="max-w-48" />
        </div>
      </header>

      {/* HERO */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-14">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Boy in school uniform with hands behind his back"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
        </div>

        {/* Content */}
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-white/90 text-sm mb-6">
            <Sparkles className="h-4 w-4" />
            Discipline · Posture · Poise
          </div>
          <h1 className="text-5xl md:text-7xl font-extrabold text-white leading-tight mb-6 drop-shadow-lg">
            Hands Behind<br />Your Back!
          </h1>
          <p className="text-xl md:text-2xl text-white/85 max-w-2xl mx-auto leading-relaxed mb-8">
            A simple gesture that transforms posture, builds confidence, and connects cultures worldwide.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href="#benefits"
              className="inline-flex items-center gap-2 bg-white text-gray-900 font-semibold px-6 py-3 rounded-full hover:bg-white/90 transition-colors shadow-lg"
            >
              Discover the Benefits
            </a>
            <a
              href="#challenges"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/30 text-white font-semibold px-6 py-3 rounded-full hover:bg-white/20 transition-colors"
            >
              Take a Challenge
            </a>
          </div>

          {/* Multilingual tagline strip */}
          <div className="mt-12 flex flex-wrap justify-center gap-x-4 gap-y-1 text-white/60 text-xs">
            {[
              'Hände auf den Rücken!',
              'Handen op de rug!',
              '手を後ろに組んで！',
              'Les mains derrière le dos !',
              '¡Manos detrás de la espalda!',
              'Mani dietro la schiena!',
              'Manojn malantaŭ via dorso!',
            ].map(phrase => (
              <span key={phrase}>{phrase}</span>
            ))}
          </div>
        </div>

        <a
          href="#benefits"
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 hover:text-white transition-colors animate-bounce"
        >
          <ChevronDown className="h-7 w-7" />
        </a>
      </section>

      {/* BENEFITS */}
      <section id="benefits" className="py-20 bg-background">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-14">
            <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              Why it matters
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              The Benefits of Hands Behind Your Back
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              A posture known across cultures, professions, and centuries — and for very good reasons.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map(({ icon: Icon, title, description }) => (
              <Card key={title} className="border hover:shadow-lg transition-shadow group">
                <CardContent className="pt-6 pb-6">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* EXAMPLES */}
      <section id="examples" className="py-20 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-14">
            <span className="inline-block px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-sm font-medium mb-4">
              In practice
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Where Hands Behind the Back Appears
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From concert stages to parade grounds, this posture has shaped disciplines around the world.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {examples.map(({ icon: Icon, category, title, desc, color, border }) => (
              <Card key={title} className={`bg-gradient-to-br ${color} ${border} border`}>
                <CardContent className="pt-6 pb-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-background/50 flex items-center justify-center shrink-0">
                      <Icon className="h-6 w-6" />
                    </div>
                    <div>
                      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{category}</span>
                      <h3 className="font-bold text-lg mt-0.5 mb-2">{title}</h3>
                      <p className="text-sm leading-relaxed text-foreground/80">{desc}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CHALLENGES */}
      <section id="challenges" className="py-20 bg-background">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-14">
            <span className="inline-block px-3 py-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm font-medium mb-4">
              Try it yourself
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Challenges
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Push your discipline, posture, and poise with these progressive challenges.
            </p>
          </div>

          <div className="space-y-4">
            {challenges.map(({ level, title, desc }) => (
              <Card key={title} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-start gap-4">
                    <CheckCircle2 className="h-5 w-5 text-muted-foreground/50 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2 mb-1">
                        <h3 className="font-semibold">{title}</h3>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${levelColour[level]}`}>
                          {level}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* NEURODIVERGENT */}
      <section id="neurodivergent" className="py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-14">
            <span className="inline-block px-3 py-1 rounded-full bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 text-sm font-medium mb-4">
              Neurodivergent perspectives
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 flex items-center justify-center gap-3">
              <Brain className="h-8 w-8 text-teal-500" />
              Neurodivergence & Hands Behind Your Back
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Autistic people, ADHDers, and others with sensory differences can experience this posture in very different — and equally valid — ways.
            </p>
          </div>

          <div className="space-y-6">
            {neurodivergent.map(({ heading, content }) => (
              <Card key={heading} className="border hover:shadow-md transition-shadow">
                <CardContent className="pt-6 pb-6">
                  <h3 className="font-bold text-lg mb-3 text-teal-700 dark:text-teal-300">{heading}</h3>
                  <p className="text-sm leading-relaxed text-foreground/80">{content}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-10 p-6 rounded-2xl bg-teal-50 dark:bg-teal-950/30 border border-teal-200 dark:border-teal-800">
            <div className="flex items-start gap-4">
              <MessageCircle className="h-6 w-6 text-teal-600 dark:text-teal-400 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-teal-800 dark:text-teal-200 mb-1">A personal note</p>
                <p className="text-sm text-teal-700/80 dark:text-teal-300/80 leading-relaxed">
                  The author of this page is an Asperger autist who personally finds keeping their hands behind their back both calming and grounding — a natural, low-effort way to feel more present and regulated. This is shared as one data point among many, not as a universal prescription. You may feel the same, differently, or not at all, and every response is equally correct.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 dark:from-gray-950 dark:to-gray-900 text-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <blockquote className="text-2xl md:text-3xl font-light leading-relaxed mb-6">
            "The body's posture speaks before the mouth opens. Hold yourself well, and the world listens."
          </blockquote>
          <p className="text-white/50 text-sm">— on the power of posture</p>
        </div>
      </section>

      {/* NOSTR SECTION */}
      <NostrSection />

      {/* FOOTER */}
      <footer className="py-10 border-t border-border/50 bg-muted/20">
        <div className="max-w-6xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} Hands Behind Your Back! — All images &amp; videos generated by AI.</p>
          <a
            href="https://marlowe.shakespeare.wtf"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-foreground transition-colors"
          >
            Vibed with Marlowe ✦
          </a>
        </div>
        {/* Hidden link — visible only to those who look for it */}
        <div className="max-w-6xl mx-auto px-4 mt-4 flex justify-center">
          <Link
            to="/bound"
            className="text-[10px] text-muted-foreground/20 hover:text-muted-foreground/60 transition-colors"
          >
            ·
          </Link>
        </div>
      </footer>
    </div>
  );
}
